import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AuthContext = createContext(null);

// Store session end callback to be called on logout
let sessionEndCallback = null;
export const setSessionEndCallback = (callback) => {
  sessionEndCallback = callback;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('smartcaddy_token'));
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);

  const api = axios.create({
    baseURL: `${API_URL}/api`,
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });

  // Update API headers when token changes
  useEffect(() => {
    if (token) {
      api.defaults.headers.Authorization = `Bearer ${token}`;
    } else {
      delete api.defaults.headers.Authorization;
    }
  }, [token, api]);

  // Load user on mount
  useEffect(() => {
    const loadUser = async () => {
      if (token) {
        try {
          const adminToken = localStorage.getItem('smartcaddy_admin_token');
          if (adminToken) {
            setIsAdmin(true);
            const adminData = JSON.parse(localStorage.getItem('smartcaddy_admin_data') || '{}');
            setUser(adminData);
          } else {
            const response = await api.get('/auth/me');
            setUser(response.data);
          }
        } catch (error) {
          console.error('Failed to load user:', error);
          logout();
        }
      }
      setLoading(false);
    };
    loadUser();
  }, []);

  const sendOtp = async (contact, contactType = 'email') => {
    const response = await api.post('/auth/send-otp', { contact, contact_type: contactType });
    return response.data;
  };

  const verifyOtp = async (contact, otp, cartId = null) => {
    const response = await api.post('/auth/verify-otp', { contact, otp, cart_id: cartId });
    const { access_token, user: userData } = response.data;
    
    localStorage.setItem('smartcaddy_token', access_token);
    setToken(access_token);
    setUser(userData);
    setIsAdmin(false);
    
    return response.data;
  };

  const adminLogin = async (email, password) => {
    const response = await api.post('/admin/login', { email, password });
    const { access_token, admin } = response.data;
    
    localStorage.setItem('smartcaddy_token', access_token);
    localStorage.setItem('smartcaddy_admin_token', access_token);
    localStorage.setItem('smartcaddy_admin_data', JSON.stringify(admin));
    setToken(access_token);
    setUser(admin);
    setIsAdmin(true);
    
    return response.data;
  };

  const logout = useCallback(async () => {
    // End active session before logging out
    if (sessionEndCallback) {
      try {
        await sessionEndCallback();
      } catch (error) {
        console.error('Error ending session on logout:', error);
      }
    }
    
    localStorage.removeItem('smartcaddy_token');
    localStorage.removeItem('smartcaddy_admin_token');
    localStorage.removeItem('smartcaddy_admin_data');
    setToken(null);
    setUser(null);
    setIsAdmin(false);
  }, []);

  const value = {
    user,
    token,
    loading,
    isAdmin,
    isAuthenticated: !!token && !!user,
    sendOtp,
    verifyOtp,
    adminLogin,
    logout,
    api,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
